import { useContext, useEffect, useCallback, useMemo, useRef, useState, memo, type ComponentType } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import { Heading4Style, Heading5Style } from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import LineArrowUpRightIcon from 'LuxKit/Icons/line/LineArrowUpRightIcon'
import LineFolderDownloadIcon from 'LuxKit/Icons/line/LineFolderDownloadIcon'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import Group from 'components/utils/Group'
import ContactContext from 'contexts/contactContext'
import { useIsMobileOrTablet } from 'hooks/useIsMobileOrTablet'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import DevAssistantRawBlock from './DevAssistantRawBlock'
import AddedBookingTiles, {
  extractAddedBookingTilesFromMessage,
} from './AddedBookingTiles'
import MarkdownRender from 'LuxKit/MarkdownRender'
import { preprocessChatMarkdown } from 'tripPlanner/utils/chatMarkdown'
import MessageContextLabel from './MessageContextLabel'
import ChatHotelCarousel from './ChatHotelCarousel'
import ChatHotelRoomOptions from './ChatHotelRoomOptions'
import ChatFlightErrorBoundary from './ChatFlightErrorBoundary'
import ChatFlightCarousel from './ChatFlightCarousel'
import ChatFlightRecommendations from './ChatFlightRecommendations'
import type { FlightSearchDrawerComponentProps } from './ChatFlightSearchContent'
import ConfirmationPromptRenderer from './ConfirmationPromptRenderer'
import { toFlightRecommendationsUIData, toFlightUIData } from './chatFlightTypes'
import type { CardOption, CardCategory } from './CardsList'
import CardsList from './CardsList'
import {
  canExpandHistoricalMultiChoice,
  resolveComposerMultiChoiceSelection,
} from './resolveComposerMultiChoiceSelection'
import ImageCardsList from './ImageCardsList'
import PillsList from './PillsList'
import { UI_COMPONENT_TYPES } from './constants'
import type { StreamedCarouselData } from 'tripPlanner/api/types'
import { stripContextTags } from 'tripPlanner/utils/contextTag'
import { CLASSIC_TRIP_MIGRATION_PROMPT_PREFIX } from 'tripPlanner/utils/classicTripMigration'
import { useMessageActions, type OfferClickSearchParams } from 'tripPlanner/assistant/contexts/ChatMessageActionsContext'
import { usePlaceSearchFailure } from 'tripPlanner/assistant/contexts/PlaceSearchFailureContext'
import { isRecord } from 'lib/object/objectUtils'
import {
  isStreamedCardsListUiComponentData,
  isStreamedCarouselUiComponentData,
  isStreamedPillsListUiComponentData,
  isStreamedTestimonialUiComponentData,
  isValidOccupancy,
  isValidDates,
} from 'tripPlanner/utils/streamedProgressiveUi'
import ChatEmbeddedAccountAccess from './EmbeddedAccountAccess'
import ExperienceVoucherWidget from './ExperienceVoucherWidget'
import ChatEmbeddedCustomFlight from './EmbeddedCustomFlight'
import Testimonial from './Testimonial'
import ChatGooglePlaceWidgetContainer from './ChatGooglePlaceWidgetContainer'
import ChatLinkHoverPreview from './ChatLinkHoverPreview'
import { getChatLinkContentKey, useChatLinkInteractions } from './useChatLinkInteractions'
import type { ExperienceVoucherUIComponentData, MessagePart } from './messageTypes'

export type { MessagePart, UIComponentData } from './messageTypes'

function getCardsListCategories(data: unknown): CardCategory | undefined {
  if (!isRecord(data)) return undefined
  const { categories } = data
  if (categories === 'generic' || categories === 'places' || categories === 'locations') {
    return categories
  }
  return undefined
}

function getCarouselOccupancy(data: unknown): Array<App.Occupants> | undefined {
  if (!isRecord(data) || !isValidOccupancy(data.occupancy)) return undefined
  return data.occupancy
}

function getCarouselDates(data: unknown): NonNullable<StreamedCarouselData['dates']> | undefined {
  if (!isRecord(data) || !isValidDates(data.dates)) return undefined
  return data.dates
}

function isFlightRecommendationsData(data: unknown): boolean {
  return isRecord(data) && data.type === UI_COMPONENT_TYPES.FLIGHT_RECOMMENDATIONS
}

type ExperienceVoucherGroup = {
  type: 'experience_voucher_group'
  vouchers: Array<{
    orderId: string
    experienceItemIds: Array<string>
    primaryExperienceItemId: string
    experienceOfferId?: string
    groupType?: 'individual_item' | 'shared_provider_booking' | 'uploaded_all_items'
    downloadAllOfferItemsVoucher?: boolean
  }>
}

type RenderItem = MessagePart | ExperienceVoucherGroup
type IndexedRenderItem = { item: RenderItem; index: number }

type AssistantThoughtCollapse = {
  thoughtEntries: Array<IndexedRenderItem>
  thoughtItemIndexes: Set<number>
}

function groupExperienceVouchers(parts: Array<MessagePart>): Array<RenderItem> {
  const result: Array<RenderItem> = []
  for (const part of parts) {
    if (part.type === 'ui_component' && isExperienceVoucherData(part.data)) {
      const last = result[result.length - 1]
      const voucherEntry = normalizeExperienceVoucherData(part.data)
      if (!voucherEntry) continue
      if (last?.type === 'experience_voucher_group') {
        last.vouchers.push(voucherEntry)
      } else {
        result.push({ type: 'experience_voucher_group', vouchers: [voucherEntry] })
      }
      continue
    }
    result.push(part)
  }
  return result
}

function getAssistantThoughtCollapse(renderItems: Array<RenderItem>, canCollapse: boolean): AssistantThoughtCollapse {
  if (!canCollapse) {
    return { thoughtEntries: [], thoughtItemIndexes: new Set() }
  }

  const thoughtEntries = renderItems
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item.type === 'text' && item.assistantTextRole === 'interim')

  return {
    thoughtEntries,
    thoughtItemIndexes: new Set(thoughtEntries.map(({ index }) => index)),
  }
}

function normalizeExperienceVoucherData(data: ExperienceVoucherUIComponentData): ExperienceVoucherGroup['vouchers'][number] | null {
  const experienceItemIds = Array.isArray(data.experienceItemIds)
    ? data.experienceItemIds.filter((id) => typeof id === 'string' && id.length > 0)
    : []
  const primaryExperienceItemId = data.primaryExperienceItemId ?? data.experienceItemId ?? experienceItemIds[0]

  if (!primaryExperienceItemId) return null

  const groupedExperienceItemIds = experienceItemIds.includes(primaryExperienceItemId)
    ? experienceItemIds
    : [primaryExperienceItemId, ...experienceItemIds]

  return {
    orderId: data.orderId,
    experienceItemIds: groupedExperienceItemIds,
    primaryExperienceItemId,
    ...(data.experienceOfferId ? { experienceOfferId: data.experienceOfferId } : {}),
    ...(data.groupType ? { groupType: data.groupType } : {}),
    ...(data.downloadAllOfferItemsVoucher ? { downloadAllOfferItemsVoucher: true } : {}),
  }
}

type UrlActionLinkData = {
  type: 'action_link'
  action: string
  label: string
}

function isSafeUrlAction(action: string): boolean {
  if (!/^(\/(?!\/)|https?:\/\/)/i.test(action)) {
    return false
  }

  try {
    const parsedUrl = new URL(action, 'https://luxuryescapes.com')
    return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:'
  } catch {
    return false
  }
}

function isUrlActionLinkData(data: unknown): data is UrlActionLinkData {
  if (!isRecord(data)) return false
  return (
    data.type === 'action_link' &&
    typeof data.action === 'string' &&
    typeof data.label === 'string' &&
    isSafeUrlAction(data.action)
  )
}

function isExperienceVoucherData(data: unknown): data is ExperienceVoucherUIComponentData {
  if (!isRecord(data)) return false

  const { primaryExperienceItemId, experienceItemId, experienceItemIds } = data

  return (
    data.type === UI_COMPONENT_TYPES.EXPERIENCE_VOUCHER &&
    typeof data.orderId === 'string' &&
    (
      typeof primaryExperienceItemId === 'string' ||
      typeof experienceItemId === 'string' ||
      (Array.isArray(experienceItemIds) && experienceItemIds.some((id) => typeof id === 'string'))
    )
  )
}

function escapeRegex(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function parseChoiceSelections(userText: string, choiceLabels: Array<string>): Array<string> {
  const selections: Array<string> = []
  for (const label of choiceLabels) {
    const pattern = new RegExp(`(^|\\s|,|\\n)${escapeRegex(label)}(\\s|,|\\n|$)`, 'i')
    if (pattern.test(userText)) {
      selections.push(label)
    }
  }
  return selections
}

function stripChoiceSelections(text: string, choiceLabels: Array<string> | undefined, isDevMode: boolean): string {
  if (isDevMode || !choiceLabels || choiceLabels.length === 0) {
    return text
  }
  let working = text
  for (const label of choiceLabels) {
    const pattern = new RegExp(`(^|\\s|,)${escapeRegex(label)}(?=\\s|,|$)`, 'gi')
    working = working.replace(pattern, '$1')
  }
  const segments = working
    .split(',')
    .map(seg => seg.trim())
    .filter(Boolean)
  return segments.join(', ').trim()
}

function formatCompactDuration(startedAt?: string, finishedAt?: string): string | undefined {
  if (!startedAt || !finishedAt) return undefined

  const started = new Date(startedAt).getTime()
  const finished = new Date(finishedAt).getTime()
  if (!Number.isFinite(started) || !Number.isFinite(finished) || finished <= started) {
    return undefined
  }

  const totalSeconds = Math.max(1, Math.round((finished - started) / 1000))
  if (totalSeconds < 60) {
    return `${totalSeconds}s`
  }

  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`
}

const ThumbnailsWrapper = styled.div`
  display: flex;
  gap: ${rem(8)};
  padding-block: 0 ${rem(8)};
  padding-inline: 0;
  overflow-x: auto;
  flex-wrap: wrap;
`

const Thumbnail = styled.div`
  position: relative;
  width: ${rem(48)};
  height: ${rem(48)};
  border-radius: ${rem(4)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const FileIcon = styled.div`
  font-size: ${rem(10)};
  text-align: center;
  word-break: break-all;
  padding: ${rem(2)};
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
`

function FileThumbnail({ file }: { file: File }) {
  const intl = useIntl()
  const imageUrl = useMemo(() => {
    if (file.type.startsWith('image/')) {
      return URL.createObjectURL(file)
    }
    return null
  }, [file])

  useEffect(() => {
    return () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl)
    }
  }, [imageUrl])

  return (
    <Thumbnail>
      {imageUrl
        ? <img src={imageUrl} alt={file.name} />
        : <FileIcon>{file.name.split('.').pop()?.toUpperCase() || intl.formatMessage({ id: 'eHfEIdFmGpPFUuFWaYEx', defaultMessage: 'File', description: 'Fallback file type label shown in the trip planner chat attachment thumbnail when no file extension is available' }).toUpperCase()}</FileIcon>}
    </Thumbnail>
  )
}

const MessageBubble = styled.div`
  width: 100%;
  position: relative;
  padding: 0;
  border-radius: 0;
  background-color: transparent;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};

  &.user {
    width: auto;
    max-width: 75%;
    padding: ${rem(8)} ${rem(18)};
    border-radius: ${rem(18)};
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }

  &.user.private {
    padding: ${rem(10)} ${rem(20)};
  }
`

const PrivateMessageOutline = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  color: ${props => props.theme.palette.neutral.default.three};
  pointer-events: none;
`

const StyledBodyText = styled(BodyText)`
  overflow-wrap: break-word;
  white-space: pre-wrap;
  margin: 0;
`

const AssistantThoughtsWrapper = styled.div`
  width: 100%;
`

const AssistantThoughtsToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
  padding: 0;
  border: 0;
  background: transparent;
  color: ${props => props.theme.palette.neutral.default.two};
  cursor: pointer;
  font-family: ${props => props.theme.font.primary.family};
  text-align: start;
  text-decoration: none;

  &:hover,
  &:focus-visible,
  &:active {
    color: ${props => props.theme.palette.neutral.default.one};
    text-decoration: none;
  }
`

const AssistantThoughtsLabel = styled.span`
  font-size: ${rem(14)};
  font-weight: 400;
  line-height: ${rem(20)};
`

const AssistantThoughtsContent = styled.div`
  margin-top: ${rem(12)};
  color: ${props => props.theme.palette.neutral.default.two};

  > * + * {
    margin-top: ${rem(12)};
  }

  .assistant-step-text,
  .assistant-step-text * {
    color: inherit;
  }
`

const MessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${rem(4)};
  width: 100%;
`

const BubbleContent = styled.div`
  width: 100%;

  > * + * {
    margin-top: ${rem(20)};
  }

  > .ui-component {
    margin-top: ${rem(8)};
  }

  > .assistant-step-text + .assistant-step-text {
    margin-top: ${rem(12)};
  }

  > ${AssistantThoughtsWrapper} + * {
    margin-top: ${rem(12)};
  }
`

const ChatMarkdownWrapper = styled.div`
  h2 {
    ${Heading4Style}
  }
  h3 {
    ${Heading5Style}
  }

  .place-tag:not(.place-tag--failed),
  .offer-tag {
    font-weight: ${props => props.theme.font.primary.weight.regular};
    text-decoration: none;
    background-image: radial-gradient(circle, currentcolor ${rem(0.5)}, transparent ${rem(0.65)});
    background-position: 0 calc(100% - ${rem(1)});
    background-repeat: repeat-x;
    background-size: ${rem(3)} ${rem(1)};
  }

  /* Two-pane desktop surface only: keep names on one line so hover preview rects stay accurate.
     Mobile/tablet surfaces omit this so long tags can wrap instead of overflowing. */
  &.with-two-pane-layout {
    .place-tag:not(.place-tag--failed),
    .offer-tag {
      white-space: nowrap;
    }
  }

  .place-tag--failed {
    background-image: none;
    font-weight: inherit;
    text-decoration: none;
  }
`

export type LocationInfoCategory = 'locations' | 'places'

export type LocationInfoData = {
  title: string
  description?: string
  emoji?: string
  placeSearch: string
  category: LocationInfoCategory
}

type ChatMessageProps = {
  messageId?: string
  role: 'user' | 'assistant'
  parts: Array<MessagePart>
  isMarkdown?: boolean
  files?: Array<File>
  rawContent?: string
  oaiConversationId?: string
  generationStatus?: string
  generationStartedAt?: string
  generationFinishedAt?: string
  chatId?: number
  isChatStreaming?: boolean
  isFinalized?: boolean
  isLastMessage?: boolean
  multiChoiceInitialSelection?: Array<string>
  /** Per-message selection: live for last message, historical for older messages */
  externalSelection?: Array<string>
  previousChoiceOptions?: Array<string>
  confirmationPromptActionSelection?: 'confirm' | 'cancel'
  showTripPlannerActions?: boolean
  openOfferLinksInNewTab?: boolean
  FlightSearchDrawerComponent?: ComponentType<FlightSearchDrawerComponentProps>
  isPrivateChat?: boolean
}

// Memoized so preprocessChatMarkdown only reruns when text changes, not on every parent render
const PreprocessedMarkdown = memo(({ text, className }: { text: string; className?: string }) => (
  <ChatMarkdownWrapper className={className}>
    <MarkdownRender content={preprocessChatMarkdown(text)} fontSize="medium" type="compact" />
  </ChatMarkdownWrapper>
))

function Message({
  messageId,
  parts,
  role,
  isMarkdown = false,
  files,
  rawContent,
  oaiConversationId,
  generationStatus,
  generationStartedAt,
  generationFinishedAt,
  chatId,
  isChatStreaming = false,
  isFinalized = true,
  isLastMessage = false,
  multiChoiceInitialSelection,
  externalSelection,
  previousChoiceOptions,
  confirmationPromptActionSelection,
  showTripPlannerActions = true,
  openOfferLinksInNewTab = false,
  FlightSearchDrawerComponent,
  isPrivateChat = false,
}: ChatMessageProps) {
  const intl = useIntl()
  const contactContext = useContext(ContactContext)
  const {
    onMultiChoiceSelectionChange,
    onOfferClick,
    onOfferHover,
    onHotelMapViewClick,
    onExperienceMapViewClick,
    onPlaceClick,
    onPlaceSearchClick,
    onLocationInfoClick,
    onScheduleCallback,
    onSendMessage,
    onAuthenticatedFollowUp,
    itineraryData,
    isDevMode,
  } = useMessageActions()

  const handleExperienceOfferClick = useCallback(
    ({ offerId, searchParams }: { offerId: string; searchParams?: OfferClickSearchParams; offerType?: string }) => onOfferClick({ offerId, searchParams, offerType: 'experience' }),
    [onOfferClick],
  )

  const safeParts = useMemo(() => parts ?? [], [parts])
  const hasParts = safeParts.length > 0
  const [showAssistantThoughts, setShowAssistantThoughts] = useState(false)

  const isUser = role === 'user'
  const shouldRenderMarkdown = role === 'assistant' && !isDevMode
  const isMobileOrTablet = useIsMobileOrTablet()
  // Match ChatTripPlannerPage two-pane surface gate: desktop surface when neither
  // mobile/tablet device nor mobile-only viewport.
  const isMobileViewport = useScreenSizeOnly('mobile')
  const isTwoPaneLayout = !isMobileOrTablet && !isMobileViewport
  const canShowChatLinkHover = isTwoPaneLayout &&
    !isUser &&
    !isDevMode &&
    isFinalized &&
    generationStatus !== 'FAILED' &&
    generationStatus !== 'CANCELLED'
  const userMessageText = useMemo(() => {
    if (!isUser) {
      return undefined
    }

    if (rawContent) {
      return rawContent
    }

    return safeParts.find(
      (part): part is Extract<MessagePart, { type: 'text' }> => part.type === 'text',
    )?.content
  }, [isUser, rawContent, safeParts])
  const addedBookingTiles = useMemo(
    () => extractAddedBookingTilesFromMessage(userMessageText),
    [userMessageText],
  )

  const markdownContainerRef = useRef<HTMLDivElement>(null)
  const chatLinkContentKey = useMemo(() => getChatLinkContentKey(safeParts), [safeParts])

  const { permanentlyFailedQueries: failedPlaceQueries } = usePlaceSearchFailure()

  const {
    hoverTarget: chatLinkHoverTarget,
    closeHoverTarget: handleChatLinkHoverClose,
    keepHoverTargetOpen: keepChatLinkHoverTargetOpen,
  } = useChatLinkInteractions({
    markdownContainerRef,
    canShowHover: canShowChatLinkHover,
    contentKey: chatLinkContentKey,
    messageId,
    failedPlaceQueries,
    onOfferClick,
    onPlaceClick,
    onPlaceSearchClick,
  })

  const handleMultiChoiceSelectionChange = useCallback((labels: Array<string>) => {
    onMultiChoiceSelectionChange(labels)
  }, [onMultiChoiceSelectionChange])

  const handleCardAction = useCallback((action: string) => {
    if (action === 'schedule_callback') {
      onScheduleCallback()
      return true
    }

    return false
  }, [onScheduleCallback])

  const handleLocationInfoClick = useCallback((option: CardOption, category: CardCategory) => {
    if (category !== 'locations' && category !== 'places') return
    const placeSearch = option.placeSearch?.trim()
    if (!placeSearch) return
    onLocationInfoClick({
      title: option.title,
      description: option.description,
      emoji: option.emoji,
      placeSearch,
      category,
    })
  }, [onLocationInfoClick])

  const handleHotelRoomOptionsOfferClick = useCallback((offerId: string) => {
    onOfferClick({ offerId })
  }, [onOfferClick])
  const tripPlannerItineraryData = showTripPlannerActions ? itineraryData : undefined
  const tripPlannerSendMessage = showTripPlannerActions ? onSendMessage : undefined
  const TripPlannerFlightSearchDrawerComponent = showTripPlannerActions ? FlightSearchDrawerComponent : undefined

  const renderPart = (part: MessagePart, index: number) => {
    if (part.type === 'trip_plan_content') {
      if (!isDevMode) {
        return null
      }
      return (
        <StyledBodyText
          key={index}
          variant="medium"
          colour="normal-contrast"
        >
          {part.content}
        </StyledBodyText>
      )
    }

    if (part.type === 'ui_component') {
      // In dev mode, render as text instead of component
      if (isDevMode) {
        const jsonText = JSON.stringify(part.data, null, 2)
        return (
          <StyledBodyText
            key={index}
            variant="medium"
            colour="normal-contrast"
          >
            {jsonText}
          </StyledBodyText>
        )
      }

      if (part.data.type === 'pills_list') {
        const isStreamingPillsList = isStreamedPillsListUiComponentData(part.data) && part.data.streamingStatus !== 'complete'
        const isLockedHistoryPillsList = !isLastMessage
        const pillSelection = resolveComposerMultiChoiceSelection(
          isLockedHistoryPillsList,
          multiChoiceInitialSelection,
          externalSelection,
        )
        return (
          <div key={index} className="ui-component">
            <PillsList
              options={part.data.options}
              select={part.data.select}
              onSelectionChange={handleMultiChoiceSelectionChange}
              disabled={isLockedHistoryPillsList}
              selection={pillSelection}
              isStreaming={isStreamingPillsList}
            />
          </div>
        )
      }

      if (part.data.type === 'cards_list') {
        const cardsData = part.data
        const categories = getCardsListCategories(cardsData)
        const isStreamingCardsList = isStreamedCardsListUiComponentData(cardsData) && cardsData.streamingStatus !== 'complete'

        const isLockedHistoryCardList = !isLastMessage
        const cardSelection = resolveComposerMultiChoiceSelection(
          isLockedHistoryCardList,
          multiChoiceInitialSelection,
          externalSelection,
        )
        const allowHistoricalCardSelection = canExpandHistoricalMultiChoice(
          isLockedHistoryCardList,
          multiChoiceInitialSelection,
        )

        if (categories === 'locations' || categories === 'places') {
          return (
            <div key={index} className="ui-component">
              <ImageCardsList
                options={cardsData.options}
                select={cardsData.select}
                onSelectionChange={handleMultiChoiceSelectionChange}
                disabled={isLockedHistoryCardList}
                selection={cardSelection}
                categories={categories}
                onInfoClick={handleLocationInfoClick}
                isStreaming={isStreamingCardsList}
                allowExpandedSelectionWhenDisabled={allowHistoricalCardSelection}
              />
            </div>
          )
        }

        return (
          <div key={index} className="ui-component">
            <CardsList
              options={cardsData.options}
              select={cardsData.select}
              onSelectionChange={handleMultiChoiceSelectionChange}
              onAction={handleCardAction}
              disabled={isLockedHistoryCardList}
              selection={cardSelection}
              categories={categories}
              onInfoClick={handleLocationInfoClick}
              isStreaming={isStreamingCardsList}
              allowExpandedSelectionWhenDisabled={allowHistoricalCardSelection}
            />
          </div>
        )
      }

      if (part.data.type === UI_COMPONENT_TYPES.CAROUSEL) {
        const carouselData = part.data
        const isStreamingCarousel = isStreamedCarouselUiComponentData(carouselData) && carouselData.streamingStatus !== 'complete'

        if (carouselData.product === 'hotel') {
          return (
            <ChatHotelCarousel
              key={index}
              offerIds={carouselData.offerIds}
              searchQuery={carouselData.title}
              occupancy={getCarouselOccupancy(carouselData)}
              searchDates={getCarouselDates(carouselData)}
              onOfferClick={onOfferClick}
              onOfferHover={onOfferHover}
              onMapViewClick={onHotelMapViewClick}
              itineraryData={tripPlannerItineraryData}
              onSendMessage={tripPlannerSendMessage}
              chatId={chatId}
              trackingKey={`${messageId ?? 'message'}-${index}-${carouselData.product}`}
              isStreaming={isStreamingCarousel}
              openOfferLinksInNewTab={openOfferLinksInNewTab}
            />
          )
        }

        if (carouselData.product === 'cruise') {
          return (
            <ChatHotelCarousel
              key={index}
              offerIds={carouselData.offerIds}
              searchQuery={carouselData.title}
              cardType="cruise"
              onOfferClick={onOfferClick}
              itineraryData={tripPlannerItineraryData}
              onSendMessage={tripPlannerSendMessage}
              chatId={chatId}
              trackingKey={`${messageId ?? 'message'}-${index}-${carouselData.product}`}
              isStreaming={isStreamingCarousel}
              openOfferLinksInNewTab={openOfferLinksInNewTab}
            />
          )
        }

        return (
          <ChatHotelCarousel
            key={index}
            offerIds={carouselData.offerIds}
            searchQuery={carouselData.title}
            searchDates={getCarouselDates(carouselData)}
            cardType="experience"
            onOfferClick={handleExperienceOfferClick}
            onOfferHover={onOfferHover}
            onMapViewClick={onExperienceMapViewClick}
            itineraryData={tripPlannerItineraryData}
            onSendMessage={tripPlannerSendMessage}
            chatId={chatId}
            trackingKey={`${messageId ?? 'message'}-${index}-${carouselData.product}`}
            isStreaming={isStreamingCarousel}
            openOfferLinksInNewTab={openOfferLinksInNewTab}
          />
        )
      }

      if (part.data.type === 'flight') {
        return (
          <div key={index} className="ui-component">
            <ChatFlightCarousel
              data={toFlightUIData(part.data)}
              onSendMessage={tripPlannerSendMessage}
              isStreaming={isChatStreaming}
              FlightSearchDrawerComponent={TripPlannerFlightSearchDrawerComponent}
            />
          </div>
        )
      }

      if (part.data.type === UI_COMPONENT_TYPES.HOTEL_ROOM_OPTIONS) {
        return (
          <div key={index} className="ui-component">
            <ChatHotelRoomOptions
              data={part.data}
              onOfferClick={showTripPlannerActions ? handleHotelRoomOptionsOfferClick : undefined}
              showViewOfferAction={showTripPlannerActions}
              itineraryData={tripPlannerItineraryData}
              onSendMessage={tripPlannerSendMessage}
            />
          </div>
        )
      }

      if (isFlightRecommendationsData(part.data)) {
        const flightRecommendationsData = toFlightRecommendationsUIData(part.data)
        if (flightRecommendationsData.items.length === 0) return null

        return (
          <div key={index} className="ui-component">
            <ChatFlightErrorBoundary>
              <ChatFlightRecommendations
                data={flightRecommendationsData}
                onSendMessage={tripPlannerSendMessage}
              />
            </ChatFlightErrorBoundary>
          </div>
        )
      }

      if (isRecord(part.data) && part.data.type === UI_COMPONENT_TYPES.SIGN_UP_MODAL) {
        return (
          <ChatEmbeddedAccountAccess
            key={index}
            isLastMessage={isLastMessage}
            onAuthenticated={onAuthenticatedFollowUp}
          />
        )
      }

      if (part.data.type === UI_COMPONENT_TYPES.CONFIRMATION_PROMPT) {
        return (
          <div key={index} className="ui-component">
            <ConfirmationPromptRenderer
              pendingActionId={part.data.pendingActionId}
              selectedAction={confirmationPromptActionSelection}
              isHistorical={!isLastMessage}
              isStreaming={isChatStreaming}
              presentation={part.data.presentation}
              onSendMessage={tripPlannerSendMessage}
            />
          </div>
        )
      }

      if (part.data.type === UI_COMPONENT_TYPES.CUSTOM_FLIGHT) {
        if (!showTripPlannerActions) {
          return null
        }

        return (
          <ChatEmbeddedCustomFlight
            key={index}
            isLastMessage={isLastMessage}
            isStreaming={isChatStreaming}
          />
        )
      }

      if (part.data.type === UI_COMPONENT_TYPES.TESTIMONIAL) {
        const isStreamingTestimonial = isStreamedTestimonialUiComponentData(part.data) && part.data.streamingStatus !== 'complete'
        return (
          <div key={index} className="ui-component">
            <Testimonial reviews={part.data.reviews} isStreaming={isStreamingTestimonial} />
          </div>
        )
      }

      if (part.data.type === UI_COMPONENT_TYPES.GOOGLE_PLACE) {
        return (
          <div key={`${messageId ?? 'message'}-google-place-${part.data.googlePlaceId}`} className="ui-component">
            <ChatGooglePlaceWidgetContainer
              data={part.data}
              onPlaceClick={onPlaceClick}
              itineraryData={tripPlannerItineraryData}
              onSendMessage={tripPlannerSendMessage}
            />
          </div>
        )
      }

      if (part.data.type === 'action_link') {
        const { action, label } = part.data

        if (isUrlActionLinkData(part.data)) {
          return (
            <TextButton
              key={index}
              kind="secondary"
              size="medium"
              href={part.data.action}
              download
              endIcon={<LineFolderDownloadIcon />}
            >
              {part.data.label}
            </TextButton>
          )
        }

        if (action === 'call_expert') {
          const defaultContact = contactContext?.defaultContact
          if (!defaultContact) {
            return null
          }
          const contactText = label
            ? intl.formatMessage(
                {
                  id: 'ow1gwGY-0_8lHK5yKaVN',
                  defaultMessage: '{label} - {contact}',
                  description: 'Contact label paired with phone number for the call expert action in trip planner chat',
                },
                { label, contact: defaultContact.humanReadable },
              )
            : intl.formatMessage(
                {
                  id: 'KoIDLbooo4tckfqqz5AJ',
                  defaultMessage: 'Call {contact}',
                  description: 'Call-to-action button text with phone number for contacting a travel expert in trip planner chat',
                },
                { contact: defaultContact.humanReadable },
              )
          return (
            <TextButton
              key={index}
              kind="secondary"
              size="medium"
              href={`tel:${defaultContact.number}`}
              endIcon={<LineArrowUpRightIcon />}
            >
              {contactText}
            </TextButton>
          )
        }

        if (action === 'schedule_callback') {
          return (
            <TextButton
              key={index}
              kind="secondary"
              size="medium"
              onClick={onScheduleCallback}
              endIcon={<LineArrowUpRightIcon />}
            >
              {label}
            </TextButton>
          )
        }

        return null
      }

      return null
    }

    let text = part.content
    if (!text || text.trim() === '') {
      return null
    }

    if (isUser && text.trimStart().startsWith(CLASSIC_TRIP_MIGRATION_PROMPT_PREFIX)) {
      text = intl.formatMessage({
        id: '_cqRLglMEfwdhZlnwXFH',
        defaultMessage: 'Help move my trip over to Luxury Escapes’ new AI chat. This may take a minute or two.',
        description: 'Friendly message shown in the chat bubble when a classic trip migration prompt is submitted',
      })
    }

    // Strip choice labels from user bubble in non-dev mode (still visible in dev via raw content)
    if (isUser) {
      text = stripChoiceSelections(text, previousChoiceOptions, isDevMode)
    }

    // For user messages, strip <Context>...</Context> tags in non-dev mode
    // These tags contain highlighted text context sent to the LLM
    if (isUser && !isDevMode) {
      text = stripContextTags(text)
      if (!text) {
        return null
      }
    }

    if (shouldRenderMarkdown) {
      return (
        <PreprocessedMarkdown
          key={index}
          text={text}
          className={clsx(
            role === 'assistant' && 'assistant-step-text',
            isTwoPaneLayout && 'with-two-pane-layout',
          )}
        />
      )
    }

    return (
      <StyledBodyText
        key={index}
        className={clsx(
          role === 'assistant' && 'assistant-step-text',
        )}
        variant="medium"
        colour={isUser ? undefined : 'normal-contrast'}
      >
        {text}
      </StyledBodyText>
    )
  }

  const renderItem = (item: RenderItem, index: number) => {
    if (item.type === 'experience_voucher_group') {
      if (isDevMode) {
        return (
          <StyledBodyText
            key={index}
            variant="medium"
            colour="neutral-one"
          >
            {JSON.stringify(item.vouchers, null, 2)}
          </StyledBodyText>
        )
      }
      return (
        <div key={index} className="ui-component">
          <ExperienceVoucherWidget vouchers={item.vouchers} />
        </div>
      )
    }
    return renderPart(item, index)
  }

  const renderItems = useMemo(() => groupExperienceVouchers(safeParts), [safeParts])
  const assistantThoughtCollapse = getAssistantThoughtCollapse(
    renderItems,
    role === 'assistant' &&
    !isDevMode &&
    isFinalized &&
    generationStatus !== 'FAILED' &&
    generationStatus !== 'CANCELLED',
  )
  const { thoughtEntries: assistantThoughtEntries, thoughtItemIndexes } = assistantThoughtCollapse

  const assistantThoughtDuration = formatCompactDuration(generationStartedAt, generationFinishedAt)
  const assistantThoughtLabel = assistantThoughtDuration
    ? intl.formatMessage({
        id: 'KRnDBtMrac6SJhkdByC0',
        defaultMessage: 'Planned for {duration}',
        description: 'Collapsed label for interim AI assistant reasoning and progress text in trip planner chat',
      }, { duration: assistantThoughtDuration })
    : intl.formatMessage({
        id: 'T5JEgkBob6bs1U3L2Oxl',
        defaultMessage: 'Thoughts',
        description: 'Collapsed label for interim AI assistant reasoning and progress text in trip planner chat when duration is unavailable',
      })

  const assistantThoughtsDisclosure = assistantThoughtEntries.length > 0
    ? (
        <AssistantThoughtsWrapper key="assistant-thoughts">
          <AssistantThoughtsToggle
            type="button"
            aria-expanded={showAssistantThoughts}
            onClick={() => setShowAssistantThoughts(open => !open)}
          >
            <AssistantThoughtsLabel>{assistantThoughtLabel}</AssistantThoughtsLabel>
            <DropdownChevron open={showAssistantThoughts} size="XS" />
          </AssistantThoughtsToggle>
          {showAssistantThoughts && (
            <AssistantThoughtsContent>
              {assistantThoughtEntries.map(({ item, index }) => renderItem(item, index))}
            </AssistantThoughtsContent>
          )}
        </AssistantThoughtsWrapper>
      )
    : null

  let assistantThoughtsInserted = false
  const messageContent = renderItems.map((item, index) => {
    if (
      assistantThoughtsDisclosure &&
      thoughtItemIndexes.has(index)
    ) {
      if (assistantThoughtsInserted) {
        return null
      }
      assistantThoughtsInserted = true
      return assistantThoughtsDisclosure
    }

    return renderItem(item, index)
  }).filter(Boolean)
  const devRawContent = isDevMode && role === 'assistant' && rawContent
    ? <DevAssistantRawBlock rawContent={rawContent} oaiConversationId={oaiConversationId} />
    : null
  const shouldRenderMessageContent = !(isDevMode && role === 'assistant')
  const hasBubbleContent = (shouldRenderMessageContent && messageContent.length > 0) ||
    Boolean(files && files.length > 0) ||
    Boolean(devRawContent)
  const hasRenderableContent = hasBubbleContent ||
    addedBookingTiles.length > 0
  const choiceSelections = useMemo(() => {
    if (!isUser || !previousChoiceOptions || previousChoiceOptions.length === 0) {
      return []
    }
    const sourceText = rawContent || safeParts.find(p => p.type === 'text')?.content || ''
    if (!sourceText) {
      return []
    }
    return parseChoiceSelections(stripContextTags(sourceText), previousChoiceOptions)
  }, [isUser, previousChoiceOptions, rawContent, safeParts])

  if (!hasParts) {
    return null
  }

  if (isMarkdown && !isDevMode) {
    return null
  }

  if (!isDevMode && !hasRenderableContent) {
    // Allow rendering context label even if the bubble is empty (e.g., choice-only submissions).
    if (!(isUser && (previousChoiceOptions?.length ?? 0) > 0)) {
      return null
    }
  }

  // Check if we should show context label (only for user messages)
  const showContextLabel = isUser

  const bubbleContent = (
    <BubbleContent ref={markdownContainerRef}>
      {files && files.length > 0 && (
        <ThumbnailsWrapper>
          {files.map((file, index) => (
            <FileThumbnail key={`${file.name}-${index}`} file={file} />
          ))}
        </ThumbnailsWrapper>
      )}
      {shouldRenderMessageContent && messageContent}
      {devRawContent}
    </BubbleContent>
  )

  const bubbleNode = hasBubbleContent
    ? <MessageBubble className={clsx({ user: isUser, private: isUser && isPrivateChat })} data-message-bubble>
        {isUser && isPrivateChat && (
          <PrivateMessageOutline aria-hidden>
            <rect
              x="1"
              y="1"
              width="calc(100% - 2px)"
              height="calc(100% - 2px)"
              rx="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="0.75"
              strokeDasharray="10 6"
            />
          </PrivateMessageOutline>
        )}
        {bubbleContent}
    </MessageBubble>
    : null
  const addedBookingTilesNode = addedBookingTiles.length > 0
    ? <AddedBookingTiles tiles={addedBookingTiles} />
    : null

  const messageWithContext = (
    <MessageWrapper>
      <MessageContextLabel parts={parts} selectionLabels={choiceSelections} />
      {bubbleNode}
      {addedBookingTilesNode}
    </MessageWrapper>
  )

  const messageWithoutContext = bubbleNode

  return (
    <Group
      direction="horizontal"
      horizontalAlign={isUser ? 'end' : 'start'}
      fullWidth
      data-message-id={messageId}
    >
      {showContextLabel ? messageWithContext : messageWithoutContext}
      {chatLinkHoverTarget && canShowChatLinkHover && (
        <ChatLinkHoverPreview
          target={chatLinkHoverTarget}
          isUser={isUser}
          isDevMode={isDevMode}
          onOfferClick={onOfferClick}
          onPlaceClick={onPlaceClick}
          onClose={handleChatLinkHoverClose}
          onMouseEnter={keepChatLinkHoverTargetOpen}
          itineraryData={tripPlannerItineraryData}
          onSendMessage={tripPlannerSendMessage}
        />
      )}
    </Group>
  )
}

export default Message
